import React, { useEffect } from "react";

const MaharishiInviteV2 = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".fade-in").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="inv2-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Source+Serif+4:ital,wght@0,400;0,500;1,400&family=IBM+Plex+Mono:wght@400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .inv2-container {
          --paper:    #F6F1E3;
          --paper-2:  #EEE5CD;
          --ink:      #221E15;
          --ink-soft: rgba(34,30,21,0.66);
          --ink-faint:rgba(34,30,21,0.42);
          --gold:     #A9791E;
          --gold-deep:#8A6114;
          --indigo:   #2E3E4E;
          --line:     rgba(34,30,21,0.16);
          --line-soft:rgba(34,30,21,0.09);

          width: 100%;
          background: var(--paper);
          color: var(--ink);
          font-family: 'Source Serif 4', serif;
          position: relative;
          overflow-x: hidden;
        }

        .fade-in { opacity:0; transform:translateY(16px); transition:opacity .6s ease, transform .6s ease; }
        .fade-in.visible { opacity:1; transform:translateY(0); }

        /* subtle paper grain */
        .inv2-container::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0.05;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size: 180px 180px;
        }

        .inv2-mono {
          font-family: 'IBM Plex Mono', monospace;
          letter-spacing: 0.08em;
        }

        /* ── left margin rule, journal-style ── */
        .inv2-margin-rule {
          position: fixed;
          top: 0; bottom: 0;
          left: 48px;
          width: 1px;
          background: var(--line-soft);
          z-index: 0;
        }
        @media (max-width: 720px) { .inv2-margin-rule { left: 22px; } }

        .inv2-hero, .inv2-section, .inv2-cta-wrap, hr.inv2-hr {
          position: relative; z-index: 1;
        }

        /* ── HERO / MASTHEAD ── */
        .inv2-hero {
          max-width: 980px;
          margin: 0 auto;
          padding: 96px 56px 64px 80px;
        }
        @media (max-width: 720px) { .inv2-hero { padding: 72px 24px 48px 48px; } }

        .inv2-meta-row {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 40px;
        }
        .inv2-meta-row .inv2-mono {
          font-size: 11px;
          color: var(--ink-soft);
          text-transform: uppercase;
        }
        .inv2-meta-rule { flex: 1; height: 1px; background: var(--line); }

        .inv2-hero-inner {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 32px;
        }
        .inv2-hero-inner > div:first-child {
          flex: 1 1 auto;
          min-width: 0;
        }
        .inv2-sigil { flex-shrink: 0; }
        @media (max-width: 860px) { .inv2-hero-inner { flex-direction: column; align-items: flex-start; } }

        .inv2-title {
          font-family: 'Fraunces', serif;
          font-optical-sizing: auto;
          font-size: clamp(52px, 9vw, 104px);
          font-weight: 600;
          color: var(--ink);
          line-height: 0.94;
          letter-spacing: -0.01em;
        }

        .inv2-fullform {
          font-family: 'IBM Plex Mono', monospace;
          font-size: clamp(11px, 1.3vw, 13px);
          color: var(--gold-deep);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-top: 18px;
          max-width: 460px;
          line-height: 1.7;
        }

        .inv2-sigil {
          width: 132px;
          height: 132px;
          flex-shrink: 0;
          opacity: 0.85;
        }
        @media (max-width: 860px) { .inv2-sigil { width: 88px; height: 88px; } }

        .inv2-tagline {
          max-width: 600px;
          font-size: clamp(16px, 1.9vw, 19px);
          color: var(--ink-soft);
          font-style: italic;
          line-height: 1.85;
          font-weight: 400;
          margin: 36px 0 44px;
          border-left: 2px solid var(--gold);
          padding-left: 20px;
        }

        /* ── SPECIMEN TAG STRIP (replaces stat strip) ── */
        .inv2-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 0;
          border-top: 1px solid var(--line);
          border-bottom: 1px solid var(--line);
          margin-bottom: 44px;
        }
        .inv2-strip-item {
          flex: 1;
          min-width: 130px;
          padding: 18px 20px;
          border-right: 1px dashed var(--line);
        }
        .inv2-strip-item:last-child { border-right: none; }
        .inv2-strip-num {
          display: block;
          font-family: 'Fraunces', serif;
          font-size: 26px;
          font-weight: 600;
          color: var(--ink);
          line-height: 1;
          margin-bottom: 6px;
        }
        .inv2-strip-label {
          display: block;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 10px;
          color: var(--ink-faint);
          text-transform: uppercase;
          letter-spacing: 0.07em;
        }
        @media (max-width: 640px) {
          .inv2-strip-item { border-right: none; border-bottom: 1px dashed var(--line); }
          .inv2-strip-item:last-child { border-bottom: none; }
        }

        /* ── BUTTONS ── */
        .inv2-actions { display:flex; gap:14px; flex-wrap:wrap; }
        .inv2-btn {
          font-family: 'IBM Plex Mono', monospace;
          padding: 13px 26px;
          font-size: 12.5px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          border-radius: 2px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          transition: background .2s, color .2s, transform .2s;
        }
        .inv2-btn-primary {
          background: var(--ink);
          color: var(--paper);
          border: 1px solid var(--ink);
        }
        .inv2-btn-primary:hover { background: var(--gold-deep); border-color: var(--gold-deep); transform: translateY(-2px); }
        .inv2-btn-outline {
          background: transparent;
          color: var(--ink);
          border: 1px solid var(--ink);
        }
        .inv2-btn-outline:hover { background: var(--ink); color: var(--paper); transform: translateY(-2px); }

        /* ── SECTIONS ── */
        .inv2-section {
          max-width: 980px;
          margin: 0 auto;
          padding: 64px 56px 64px 80px;
        }
        @media (max-width: 720px) { .inv2-section { padding: 48px 24px 48px 48px; } }

        .inv2-eyebrow {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--gold-deep);
          margin-bottom: 14px;
          font-weight: 500;
        }
        .inv2-sec-title {
          font-family: 'Fraunces', serif;
          font-size: clamp(28px, 3.6vw, 44px);
          color: var(--ink);
          font-weight: 600;
          margin-bottom: 18px;
          line-height: 1.1;
          max-width: 620px;
        }
        .inv2-sec-desc {
          font-size: 16px;
          color: var(--ink-soft);
          line-height: 1.85;
          font-weight: 400;
          max-width: 640px;
          margin-bottom: 44px;
        }

        hr.inv2-hr {
          border: none;
          border-top: 1px solid var(--line);
          margin: 0 56px 0 80px;
        }
        @media (max-width: 720px) { hr.inv2-hr { margin: 0 24px 0 48px; } }

        /* ── VISION / MISSION ── */
        .inv2-vm-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--line);
          border: 1px solid var(--line);
        }
        @media (max-width: 720px) { .inv2-vm-grid { grid-template-columns: 1fr; } }

        .inv2-vm-card {
          background: var(--paper);
          padding: 32px 30px;
        }
        .inv2-vm-card-label {
          font-family: 'Fraunces', serif;
          font-size: clamp(22px, 2.4vw, 28px);
          letter-spacing: 0;
          text-transform: none;
          color: var(--ink);
          margin-bottom: 6px;
          display: block;
          font-weight: 600;
        }
        .inv2-vm-card-label::after {
          content: "";
          display: block;
          width: 28px;
          height: 2px;
          background: var(--gold);
          margin-top: 10px;
          margin-bottom: 8px;
        }
          
        .inv2-vm-card h3 {
          font-family: 'Fraunces', serif;
          font-size: clamp(20px, 2.2vw, 25px);
          color: var(--ink);
          font-weight: 600;
          margin-bottom: 14px;
          line-height: 1.2;
        }
        .inv2-vm-card p {
          font-size: 15px;
          color: var(--ink-soft);
          line-height: 1.85;
          font-weight: 400;
        }

        /* ── WHY IT MATTERS / SDG TAGS ── */
        .inv2-sdg-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 36px;
        }
        .inv2-sdg-tag {
          font-family: 'IBM Plex Mono', monospace;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid var(--line);
          color: var(--ink-soft);
          font-size: 11.5px;
          padding: 7px 12px;
          border-radius: 2px;
          letter-spacing: 0.02em;
        }
        .inv2-sdg-tag::before {
          content: "§";
          color: var(--gold-deep);
          font-weight: 600;
        }
        .inv2-matter-desc {
          max-width: 680px;
          font-size: 15px;
          color: var(--ink-soft);
          line-height: 1.9;
          font-weight: 400;
        }
        .inv2-matter-desc em {
          font-style: italic;
          color: var(--gold-deep);
        }

        /* ── CTA (styled as a sealed document) ── */
        .inv2-cta-wrap { padding: 0 56px 88px 80px; }
        @media (max-width: 720px) { .inv2-cta-wrap { padding: 0 24px 64px 48px; } }

        .inv2-cta {
          max-width: 860px;
          margin: 0 auto;
          border: 1px solid var(--ink);
          padding: 56px 44px;
          position: relative;
          text-align: center;
        }
        .inv2-cta p,
        .inv2-cta .inv2-footnote {
          margin-left: auto;
          margin-right: auto;
        }
        .inv2-cta-btns { justify-content: center; }
        .inv2-cta::before {
          content: "";
          position: absolute;
          inset: 8px;
          border: 1px solid var(--line);
          pointer-events: none;
        }
        .inv2-cta h2 {
          font-family: 'Fraunces', serif;
          font-size: clamp(26px, 3.4vw, 38px);
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 16px;
        }
        .inv2-cta p {
          font-size: 15px;
          color: var(--ink-soft);
          max-width: 540px;
          margin-bottom: 32px;
          line-height: 1.8;
          font-weight: 400;
        }
        .inv2-cta-btns { display:flex; gap:14px; flex-wrap:wrap; margin-bottom: 28px; }

        .inv2-footnote {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11.5px;
          color: var(--ink-faint);
          line-height: 1.8;
          max-width: 460px;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .inv2-footnote a { color: var(--gold-deep); text-decoration: none; }
        .inv2-footnote a:hover { text-decoration: underline; }
      `}</style>

      <div className="inv2-margin-rule" />

      {/* ── HERO / MASTHEAD ── */}
      <section className="inv2-hero">
        <div className="inv2-meta-row fade-in">
          <span className="inv2-mono">🌾 G20 MACS Endorsed · Proposed by India</span>
          <span className="inv2-meta-rule" />
        </div>

        <div className="inv2-hero-inner">
          <div>
            <h1 className="inv2-title fade-in">MAHARISHI</h1>
            <p className="inv2-fullform fade-in">
              Millets and Other Ancient Grains International Research Initiative
            </p>
          </div>

          {/* signature element — line-art grain stalk, the one visual risk */}
          <svg className="inv2-sigil fade-in" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 112 C60 90 60 70 60 50" stroke="#221E15" strokeOpacity="0.55" strokeWidth="1.4"/>
            <path d="M60 50 C48 44 40 34 38 20" stroke="#A9791E" strokeOpacity="0.8" strokeWidth="1.4"/>
            <path d="M60 50 C72 44 80 34 82 20" stroke="#A9791E" strokeOpacity="0.8" strokeWidth="1.4"/>
            <path d="M60 62 C50 57 44 49 42 38" stroke="#221E15" strokeOpacity="0.4" strokeWidth="1.2"/>
            <path d="M60 62 C70 57 76 49 78 38" stroke="#221E15" strokeOpacity="0.4" strokeWidth="1.2"/>
            <ellipse cx="38" cy="19" rx="4.5" ry="7" transform="rotate(-18 38 19)" fill="#A9791E" fillOpacity="0.75"/>
            <ellipse cx="82" cy="19" rx="4.5" ry="7" transform="rotate(18 82 19)" fill="#A9791E" fillOpacity="0.75"/>
            <ellipse cx="42" cy="37" rx="3.6" ry="5.6" transform="rotate(-18 42 37)" fill="#221E15" fillOpacity="0.35"/>
            <ellipse cx="78" cy="37" rx="3.6" ry="5.6" transform="rotate(18 78 37)" fill="#221E15" fillOpacity="0.35"/>
          </svg>
        </div>

        <p className="inv2-tagline fade-in">
          A global platform for research, innovation, and collaboration
          in millets and ancient grains — inviting nations, institutions, and
          organisations to join as Members and shape the future of
          sustainable food systems together.
        </p>

        <div className="inv2-strip fade-in">
          <div className="inv2-strip-item">
            <span className="inv2-strip-num">G20</span>
            <span className="inv2-strip-label">Nations Backing</span>
          </div>
          <div className="inv2-strip-item">
            <span className="inv2-strip-num">10–12</span>
            <span className="inv2-strip-label">MoUs at Launch</span>
          </div>
          <div className="inv2-strip-item">
            <span className="inv2-strip-num">10+</span>
            <span className="inv2-strip-label">SDGs Aligned</span>
          </div>
          <div className="inv2-strip-item">
            <span className="inv2-strip-num">Open</span>
            <span className="inv2-strip-label">Membership</span>
          </div>
        </div>

        <div className="inv2-actions fade-in">
          <a href="/magic2026_registration" className="inv2-btn inv2-btn-primary">Express Interest to Join</a>
          <a href="/magic" className="inv2-btn inv2-btn-outline">Explore MAGIC 2026 →</a>
        </div>
      </section>

      <hr className="inv2-hr" />

      {/* ── VISION & MISSION ── */}
      <section className="inv2-section">
        <p className="inv2-eyebrow fade-in">Foundation</p>
        <h2 className="inv2-sec-title fade-in">Our Vision & Mission</h2>
        <p className="inv2-sec-desc fade-in">
          MAHARISHI was born from a simple but powerful idea — that millets and ancient
          grains, once the backbone of civilisations, can once again anchor the world's
          food security, nutrition, and climate resilience.
        </p>

        <div className="inv2-vm-grid fade-in">
          <div className="inv2-vm-card">
            <span className="inv2-vm-card-label">Vision</span>
            <h3>&nbsp;</h3>
            <p>
              To cultivate a globally connected and forward-looking research ecosystem that unlocks the full potential of millets and ancient grains through collaborative knowledge exchange, innovation, and inclusive partnerships — ensuring sustainable agriculture, resilient food systems, and healthier communities for generations to come.
            </p>
          </div>
          <div className="inv2-vm-card">
            <span className="inv2-vm-card-label">Mission</span>
            <h3>&nbsp;</h3>
            <p>
              To establish robust mechanisms for connecting global researchers and institutions working on millets and ancient grains, enabling effective knowledge exchange, identification of research gaps, and dissemination of innovations. Through digital platforms, collaborative workshops, open-access publications, and recognition of young scientists, the initiative aims to accelerate research, build capacity, and raise awareness of the nutritional, cultural, and climate-resilient value of these crops — empowering smallholder farmers and advancing sustainable food systems.
            </p>
          </div>
        </div>
      </section>

      <hr className="inv2-hr" />

      {/* ── WHY IT MATTERS ── */}
      <section className="inv2-section">
        <p className="inv2-eyebrow fade-in">Why It Matters</p>
        <h2 className="inv2-sec-title fade-in">Millets Are a Global Imperative</h2>
        <p className="inv2-sec-desc fade-in">
          As climate change threatens major food crops, millets and ancient grains —
          drought-tolerant, nutrient-rich, and water-efficient — offer a proven,
          scalable solution. They are directly linked to the achievement of 10 UN SDGs.
        </p>

        <div className="inv2-sdg-row fade-in">
          {[
            "SDG 1 – No Poverty", "SDG 2 – Zero Hunger",
            "SDG 3 – Good Health", "SDG 5 – Gender Equality",
            "SDG 6 – Clean Water", "SDG 8 – Decent Work",
            "SDG 12 – Responsible Consumption", "SDG 13 – Climate Action",
            "SDG 15 – Life on Land", "SDG 17 – Partnerships",
          ].map((s) => (
            <span className="inv2-sdg-tag" key={s}>{s}</span>
          ))}
        </div>

        <p className="inv2-matter-desc fade-in">
          In Africa and Asia, over 70% of sorghum and 95% of millets are consumed
          as staple foods. Honoured as <em>&#39;Shree-anna&#39;</em> (divine food)
          in the Indian Parliament in 2023, millets represent not just a crop — but a
          movement toward food sovereignty, cultural pride, and planetary health.
        </p>
      </section>

      <hr className="inv2-hr" />

      {/* ── CTA ── */}
      <div className="inv2-cta-wrap">
        <div className="inv2-cta fade-in">
          <h2>Join the Magic 2026</h2>
          <p>
            Become a founding partner of MAHARISHI and help shape the world's first
            permanent multilateral research initiative for millets and ancient grains.
            Sign your MoU at MAGIC 2026 in Hyderabad.
          </p>

          <p className="inv2-footnote">
            Secretariat hosted at ICAR-IIMR, Hyderabad under India's G20 Presidency mandate.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaharishiInviteV2;